import { useMemo, useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@ouropix/ui';
import { Button } from '@ouropix/ui';
import { Badge } from '@ouropix/ui';
import { ConfirmModal } from '@ouropix/ui';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@ouropix/ui';
import { Textarea } from '@ouropix/ui';
import { Label } from '@ouropix/ui';
import { Input } from '@ouropix/ui';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@ouropix/ui';
import { toast } from 'sonner';
import {
  Banknote,
  Check,
  X,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  Shield,
  Filter,
  Eye,
  History,
} from 'lucide-react';
import { ModeFilter, AdminMode } from '@/components/ui/ModeFilter';
import { TestBadge } from '@/components/ui/TestBadge';

export const Route = createFileRoute('/_admin/saques')({
  component: SaquesPage,
});

interface WithdrawalItem {
  id: string;
  user_id: string;
  user_masked: string;
  cpf_masked: string;
  kyc_level: number;
  amount: string;
  pix_key: string;
  pix_key_type: string;
  status: string;
  requires_manual_review: boolean;
  review_reason: string;
  device_risk_score: number;
  rejection_reason: string;
  reviewed_by: string;
  reviewed_at: string;
  provider: string;
  created_at: string;
  updated_at: string;
  is_test?: boolean;
}

interface WithdrawalDetail extends WithdrawalItem {
  external_reference: string;
  processed_at: string;
  attempts: Array<{
    id: string;
    status: string;
    created_at: string;
    amount: string;
    rejection_reason: string;
  }>;
}

function mapApiWithdrawal(w: any): WithdrawalItem {
  return {
    id: w.id,
    user_id: w.userId ?? w.user_id ?? '',
    user_masked: w.userMasked ?? w.user_masked ?? '***',
    cpf_masked: w.cpfMasked ?? w.cpf_masked ?? '',
    kyc_level: Number(w.kycLevel ?? w.kyc_level ?? 0),
    amount: w.amount ?? '0',
    pix_key: w.pixKey ?? w.pix_key ?? '',
    pix_key_type: w.pixKeyType ?? w.pix_key_type ?? '',
    status: w.status ?? 'pending',
    requires_manual_review:
      w.requiresManualReview ?? w.requires_manual_review ?? false,
    review_reason: w.reviewReason ?? w.review_reason ?? '',
    device_risk_score: Number(w.deviceRiskScore ?? w.device_risk_score ?? 0),
    rejection_reason: w.rejectionReason ?? w.rejection_reason ?? '',
    reviewed_by: w.reviewedBy ?? w.reviewed_by ?? '',
    reviewed_at: w.reviewedAt ?? w.reviewed_at ?? '',
    provider: w.provider ?? '',
    created_at: w.createdAt ?? w.created_at ?? '',
    updated_at: w.updatedAt ?? w.updated_at ?? '',
    is_test: w.isTest ?? w.is_test ?? false,
  };
}

function mapApiDetail(w: any): WithdrawalDetail {
  return {
    ...mapApiWithdrawal(w),
    external_reference: w.externalReference ?? w.external_reference ?? '',
    processed_at: w.processedAt ?? w.processed_at ?? '',
    attempts: Array.isArray(w.attempts)
      ? w.attempts.map((a: any) => ({
          id: a.id ?? '',
          status: a.status ?? '',
          created_at: a.createdAt ?? a.created_at ?? '',
          amount: a.amount ?? '0',
          rejection_reason: a.rejectionReason ?? a.rejection_reason ?? '',
        }))
      : [],
  };
}

function maskPixKey(key: string): string {
  if (!key) return '--';
  if (key.length <= 6) return key;
  return key.slice(0, 3) + '***' + key.slice(-3);
}

function formatCurrency(value: string | number): string {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return 'R$ 0,00';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(num);
}

function formatDate(iso: string): string {
  if (!iso) return '--';
  try {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function kycBadge(level: number) {
  const labels: Record<number, { label: string; cls: string }> = {
    0: { label: 'KYC 0', cls: 'border-gray-400 text-gray-500' },
    1: { label: 'KYC 1', cls: 'border-blue-500 text-blue-500' },
    2: { label: 'KYC 2', cls: 'border-green-500 text-green-600' },
    3: { label: 'KYC 3', cls: 'border-emerald-600 text-emerald-700' },
  };
  const cfg = labels[level] ?? labels[0];
  return (
    <Badge variant="outline" className={`text-xs ${cfg.cls}`}>
      {cfg.label}
    </Badge>
  );
}

const PAGE_SIZE = 20;

interface Filters {
  status: string;
  date_from: string;
  date_to: string;
  min_amount: string;
  max_amount: string;
  user_id: string;
  min_risk_score: string;
  max_risk_score: string;
}

const EMPTY_FILTERS: Filters = {
  status: '',
  date_from: '',
  date_to: '',
  min_amount: '',
  max_amount: '',
  user_id: '',
  min_risk_score: '',
  max_risk_score: '',
};

function SaquesPage() {
  const queryClient = useQueryClient();
  const [currentPage, setCurrentPage] = useState(1);
  const [approveId, setApproveId] = useState<string | null>(null);
  const [rejectId, setRejectId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [draftFilters, setDraftFilters] = useState<Filters>(EMPTY_FILTERS);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [mode, setMode] = useState<AdminMode>('all');

  const queryParams = useMemo(() => {
    const params: Record<string, string | number> = {
      page: currentPage,
      limit: PAGE_SIZE,
    };
    if (filters.status) params.status = filters.status;
    if (filters.date_from) params.date_from = filters.date_from;
    if (filters.date_to) params.date_to = filters.date_to;
    if (filters.min_amount) params.min_amount = filters.min_amount;
    if (filters.max_amount) params.max_amount = filters.max_amount;
    if (filters.user_id) params.user_id = filters.user_id;
    if (filters.min_risk_score) params.min_risk_score = filters.min_risk_score;
    if (filters.max_risk_score) params.max_risk_score = filters.max_risk_score;
    if (mode !== 'all') params.mode = mode;
    return params;
  }, [currentPage, filters, mode]);

  const { data, isLoading } = useQuery<{
    withdrawals: WithdrawalItem[];
    total: number;
  }>({
    queryKey: ['admin-pending-withdrawals', queryParams, mode],
    queryFn: async () => {
      const res = await api.get('/admin/withdrawals/pending', {
        params: queryParams,
      });
      const raw = res.data?.withdrawals ?? res.data?.data ?? [];
      const total = res.data?.total ?? res.data?.pagination?.total ?? raw.length ?? 0;
      return {
        withdrawals: (Array.isArray(raw) ? raw : []).map(mapApiWithdrawal),
        total,
      };
    },
  });

  const { data: detailData, isLoading: detailLoading } = useQuery<WithdrawalDetail>({
    queryKey: ['admin-withdrawal-detail', detailId],
    enabled: !!detailId,
    queryFn: async () => {
      const res = await api.get(`/admin/withdrawals/${detailId}`);
      return mapApiDetail(res.data);
    },
  });

  const withdrawals = data?.withdrawals ?? [];
  const totalPages = Math.max(1, Math.ceil((data?.total ?? 0) / PAGE_SIZE));

  const approveMutation = useMutation({
    mutationFn: async (id: string) =>
      api.post(`/admin/withdrawals/${id}/approve`),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['admin-pending-withdrawals'],
      });
      toast.success('Saque aprovado! PIX sera processado.');
      setApproveId(null);
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.message ?? 'Erro ao aprovar saque';
      if (err?.response?.status === 412) {
        toast.error(`Nao e possivel aprovar: ${msg}`);
      } else {
        toast.error(msg);
      }
    },
  });

  const rejectMutation = useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) =>
      api.post(`/admin/withdrawals/${id}/reject`, { reason }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['admin-pending-withdrawals'],
      });
      toast.success('Saque rejeitado. Saldo estornado se necessario.');
      setRejectId(null);
      setRejectReason('');
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message ?? 'Erro ao rejeitar saque');
    },
  });

  const statusBadge = (status: string, manualReview: boolean) => {
    if (manualReview || status === 'manual_review') {
      return (
        <Badge
          variant="outline"
          className="border-orange-500 text-orange-500 gap-1"
        >
          <AlertTriangle className="h-3 w-3" />
          Revisao Manual
        </Badge>
      );
    }
    if (status === 'pending') {
      return (
        <Badge variant="outline" className="border-yellow-500 text-yellow-500">
          Pendente
        </Badge>
      );
    }
    if (status === 'escalated') {
      return (
        <Badge variant="outline" className="border-purple-500 text-purple-500">
          Escalado
        </Badge>
      );
    }
    return <Badge variant="secondary">{status}</Badge>;
  };

  const applyFilters = () => {
    setFilters(draftFilters);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setDraftFilters(EMPTY_FILTERS);
    setFilters(EMPTY_FILTERS);
    setCurrentPage(1);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <Banknote className="h-5 w-5 text-primary" />
          <div>
            <h1 className="text-2xl font-bold">Saques Pendentes</h1>
            <p className="text-muted-foreground text-sm">
              Revisao manual de solicitacoes de saque
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <ModeFilter value={mode} onChange={setMode} />
          <Link to="/saques/historico">
            <Button variant="outline" size="sm" className="gap-2">
              <History className="h-4 w-4" />
              Historico
            </Button>
          </Link>
        </div>
      </div>

      {/* Filtros */}
      <div className="rounded-md border bg-card p-4 space-y-4">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Filter className="h-4 w-4" />
          Filtros
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Status</Label>
            <Select
              value={draftFilters.status || 'all'}
              onValueChange={(v) =>
                setDraftFilters((f) => ({
                  ...f,
                  status: v === 'all' ? '' : v,
                }))
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos pendentes</SelectItem>
                <SelectItem value="pending">Pendente</SelectItem>
                <SelectItem value="manual_review">Revisao Manual</SelectItem>
                <SelectItem value="escalated">Escalado</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">De</Label>
            <Input
              type="date"
              value={draftFilters.date_from}
              onChange={(e) =>
                setDraftFilters((f) => ({ ...f, date_from: e.target.value }))
              }
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Ate</Label>
            <Input
              type="date"
              value={draftFilters.date_to}
              onChange={(e) =>
                setDraftFilters((f) => ({ ...f, date_to: e.target.value }))
              }
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">ID do usuario (UUID)</Label>
            <Input
              type="text"
              placeholder="uuid..."
              value={draftFilters.user_id}
              onChange={(e) =>
                setDraftFilters((f) => ({ ...f, user_id: e.target.value.trim() }))
              }
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Valor minimo (R$)</Label>
            <Input
              type="number"
              inputMode="decimal"
              min="0"
              step="0.01"
              placeholder="0"
              value={draftFilters.min_amount}
              onChange={(e) =>
                setDraftFilters((f) => ({ ...f, min_amount: e.target.value }))
              }
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Valor maximo (R$)</Label>
            <Input
              type="number"
              inputMode="decimal"
              min="0"
              step="0.01"
              placeholder="10000"
              value={draftFilters.max_amount}
              onChange={(e) =>
                setDraftFilters((f) => ({ ...f, max_amount: e.target.value }))
              }
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Risco minimo (0-100)</Label>
            <Input
              type="number"
              inputMode="numeric"
              min="0"
              max="100"
              step="1"
              placeholder="0"
              value={draftFilters.min_risk_score}
              onChange={(e) =>
                setDraftFilters((f) => ({ ...f, min_risk_score: e.target.value }))
              }
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Risco maximo (0-100)</Label>
            <Input
              type="number"
              inputMode="numeric"
              min="0"
              max="100"
              step="1"
              placeholder="100"
              value={draftFilters.max_risk_score}
              onChange={(e) =>
                setDraftFilters((f) => ({ ...f, max_risk_score: e.target.value }))
              }
            />
          </div>
        </div>
        <div className="flex gap-2">
          <Button size="sm" onClick={applyFilters}>
            Aplicar
          </Button>
          <Button size="sm" variant="outline" onClick={clearFilters}>
            Limpar
          </Button>
        </div>
      </div>

      {/* Tabela */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Usuario</TableHead>
              <TableHead className="hidden md:table-cell">CPF</TableHead>
              <TableHead>Valor</TableHead>
              <TableHead className="hidden md:table-cell">Chave PIX</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="hidden lg:table-cell">KYC</TableHead>
              <TableHead className="hidden lg:table-cell">Risco</TableHead>
              <TableHead className="hidden xl:table-cell">Provider</TableHead>
              <TableHead className="hidden sm:table-cell">Data</TableHead>
              <TableHead className="text-right">Acoes</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell
                  colSpan={10}
                  className="text-center py-8 text-muted-foreground"
                >
                  Carregando...
                </TableCell>
              </TableRow>
            ) : withdrawals.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={10}
                  className="text-center py-12 text-muted-foreground"
                >
                  <Shield className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  Nenhum saque pendente de revisao
                </TableCell>
              </TableRow>
            ) : (
              withdrawals.map((w) => (
                <TableRow key={w.id}>
                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      <span className="font-medium">{w.user_masked}</span>
                      <TestBadge isTest={!!w.is_test} size="xs" />
                    </div>
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    <span className="font-mono text-sm text-muted-foreground">
                      {w.cpf_masked || '--'}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="font-mono font-semibold">
                      {formatCurrency(w.amount)}
                    </span>
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    <div className="flex flex-col">
                      <span className="text-xs text-muted-foreground uppercase">
                        {w.pix_key_type || 'pix'}
                      </span>
                      <span className="font-mono text-sm">
                        {maskPixKey(w.pix_key)}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      {statusBadge(w.status, w.requires_manual_review)}
                      {w.review_reason && (
                        <p className="text-xs text-muted-foreground">
                          {w.review_reason.slice(0, 40)}{w.review_reason.length > 40 ? '...' : ''}
                        </p>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="hidden lg:table-cell">
                    {kycBadge(w.kyc_level)}
                  </TableCell>
                  <TableCell className="hidden lg:table-cell">
                    <span
                      className={
                        w.device_risk_score > 70
                          ? 'text-destructive font-semibold'
                          : w.device_risk_score > 40
                            ? 'text-orange-500'
                            : 'text-muted-foreground'
                      }
                    >
                      {w.device_risk_score}
                    </span>
                  </TableCell>
                  <TableCell className="hidden xl:table-cell">
                    {w.provider ? (
                      <Badge variant="secondary" className="text-xs uppercase">
                        {w.provider}
                      </Badge>
                    ) : (
                      <span className="text-muted-foreground text-xs">--</span>
                    )}
                  </TableCell>
                  <TableCell className="hidden sm:table-cell text-sm text-muted-foreground">
                    {formatDate(w.created_at)}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        title="Ver detalhes"
                        onClick={() => setDetailId(w.id)}
                      >
                        <Eye className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-green-600 border-green-600 hover:bg-green-50"
                        disabled={approveMutation.isPending && approveId === w.id}
                        onClick={() => setApproveId(w.id)}
                      >
                        <Check className="h-3 w-3 mr-1" />
                        Aprovar
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-destructive border-destructive hover:bg-destructive/10"
                        onClick={() => {
                          setRejectId(w.id);
                          setRejectReason('');
                        }}
                      >
                        <X className="h-3 w-3 mr-1" />
                        Rejeitar
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Paginacao */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Pagina {currentPage} de {totalPages}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              disabled={currentPage <= 1}
              onClick={() => setCurrentPage((p) => p - 1)}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm">
              {currentPage} / {totalPages}
            </span>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage((p) => p + 1)}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Detail dialog */}
      <Dialog
        open={!!detailId}
        onOpenChange={(open) => { if (!open) setDetailId(null); }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Detalhes do Saque</DialogTitle>
          </DialogHeader>
          {detailLoading ? (
            <p className="text-sm text-muted-foreground py-4 text-center">Carregando...</p>
          ) : detailData ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <span className="text-muted-foreground">Usuario</span>
                <span className="font-mono">{detailData.user_masked}</span>
                <span className="text-muted-foreground">CPF</span>
                <span className="font-mono">{detailData.cpf_masked || '--'}</span>
                <span className="text-muted-foreground">KYC</span>
                <span>{kycBadge(detailData.kyc_level)}</span>
                <span className="text-muted-foreground">Valor</span>
                <span className="font-semibold">{formatCurrency(detailData.amount)}</span>
                <span className="text-muted-foreground">Chave PIX</span>
                <span className="font-mono">{detailData.pix_key} ({detailData.pix_key_type})</span>
                <span className="text-muted-foreground">Status</span>
                <span>{statusBadge(detailData.status, detailData.requires_manual_review)}</span>
                <span className="text-muted-foreground">Score de risco</span>
                <span
                  className={
                    detailData.device_risk_score > 70
                      ? 'text-destructive font-semibold'
                      : detailData.device_risk_score > 40
                        ? 'text-orange-500'
                        : ''
                  }
                >
                  {detailData.device_risk_score}
                </span>
                <span className="text-muted-foreground">Provider</span>
                <span className="uppercase">{detailData.provider || '--'}</span>
                <span className="text-muted-foreground">Ref. externa</span>
                <span className="font-mono text-xs break-all">{detailData.external_reference || '--'}</span>
                <span className="text-muted-foreground">Motivo review</span>
                <span>{detailData.review_reason || '--'}</span>
                {detailData.rejection_reason && (
                  <>
                    <span className="text-muted-foreground">Motivo rejeicao</span>
                    <span className="text-destructive">{detailData.rejection_reason}</span>
                  </>
                )}
                {detailData.reviewed_at && (
                  <>
                    <span className="text-muted-foreground">Revisado em</span>
                    <span>{formatDate(detailData.reviewed_at)}</span>
                  </>
                )}
                <span className="text-muted-foreground">Criado em</span>
                <span>{formatDate(detailData.created_at)}</span>
              </div>

              {detailData.attempts.length > 0 && (
                <div>
                  <p className="text-sm font-medium mb-2">
                    Tentativas anteriores ({detailData.attempts.length})
                  </p>
                  <div className="space-y-1 max-h-40 overflow-y-auto">
                    {detailData.attempts.map((a) => (
                      <div
                        key={a.id}
                        className="flex items-center justify-between text-xs border rounded px-2 py-1"
                      >
                        <span className="text-muted-foreground">{formatDate(a.created_at)}</span>
                        <span className="font-mono">{formatCurrency(a.amount)}</span>
                        <Badge variant="secondary" className="text-xs">{a.status}</Badge>
                        {a.rejection_reason && (
                          <span className="text-destructive truncate max-w-[120px]">{a.rejection_reason}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailId(null)}>Fechar</Button>
            {detailData && (
              <>
                <Button
                  variant="outline"
                  className="text-green-600 border-green-600 hover:bg-green-50"
                  onClick={() => {
                    setDetailId(null);
                    setApproveId(detailData.id);
                  }}
                >
                  <Check className="h-3 w-3 mr-1" />
                  Aprovar
                </Button>
                <Button
                  variant="outline"
                  className="text-destructive border-destructive hover:bg-destructive/10"
                  onClick={() => {
                    setDetailId(null);
                    setRejectId(detailData.id);
                    setRejectReason('');
                  }}
                >
                  <X className="h-3 w-3 mr-1" />
                  Rejeitar
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approve confirmation */}
      <ConfirmModal
        isOpen={!!approveId}
        onClose={() => setApproveId(null)}
        onConfirm={() => {
          if (approveId) approveMutation.mutate(approveId);
        }}
        title="Aprovar Saque"
        description="Tem certeza que deseja aprovar este saque? O PIX sera transferido para a chave do usuario via EFI."
        confirmText="Aprovar"
        cancelText="Cancelar"
      />

      {/* Reject dialog */}
      <Dialog
        open={!!rejectId}
        onOpenChange={(open) => {
          if (!open) {
            setRejectId(null);
            setRejectReason('');
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Rejeitar Saque</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="reject-reason">Motivo da rejeicao *</Label>
              <Textarea
                id="reject-reason"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Descreva o motivo da rejeicao..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setRejectId(null);
                setRejectReason('');
              }}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              disabled={!rejectReason.trim() || rejectMutation.isPending}
              onClick={() => {
                if (rejectId && rejectReason.trim()) {
                  rejectMutation.mutate({
                    id: rejectId,
                    reason: rejectReason,
                  });
                }
              }}
            >
              {rejectMutation.isPending ? 'Rejeitando...' : 'Rejeitar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
